import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { contar, prefiereQuieto } from '../utils/motion';
import { pesos as money } from '../utils/muestra';

// Demo ilustrativa del hero. Cuenta la historia una sola vez (~4 s) y se queda
// en el estado final:
//   1) entran tres movimientos y cada uno se categoriza solo,
//   2) sube "Gastos" del periodo,
//   3) aparece el aviso de gastos hormiga con lo que suman al año,
//   4) se actualiza "Disponible hoy".
// Arranca cuando el mock entra en pantalla (en móvil queda bajo el pliegue).
// Con prefers-reduced-motion se ve directamente el estado final.
// Todas las cifras son de muestra; la etiqueta "Ejemplo ilustrativo" va visible.
const FILAS = [
  { name: 'OXXO', cat: 'Tienda de conveniencia', amount: '-$48' },
  { name: 'Café', cat: 'Cafetería', amount: '-$65' },
  { name: 'Uber', cat: 'Transporte', amount: '-$89' },
];
const GASTOS = 7430;
const HORMIGA = 1260; // 14 compras chicas del mes (muestra)

export default function HeroDemo() {
  const raiz = useRef(null);
  const gas = useRef(null);
  const hor = useRef(null);
  const disp = useRef(null);
  const [fase, setFase] = useState(() => (prefiereQuieto() ? null : 'espera'));

  // Antes del primer pintado: cifras en cero, sin parpadeo del valor final.
  useLayoutEffect(() => {
    if (fase !== 'espera') return;
    if (gas.current) gas.current.textContent = '$0';
    if (hor.current) hor.current.textContent = '$0';
    if (disp.current) disp.current.textContent = '$0';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dispara al entrar en pantalla.
  useEffect(() => {
    if (fase !== 'espera') return;
    const el = raiz.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setFase('juega'); return; }
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setFase('juega'); obs.disconnect(); }
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [fase]);

  // Cuentas de los números, alineadas con los delays de index.css (.hd-*).
  useEffect(() => {
    if (fase !== 'juega') return;
    const paradas = [
      contar(gas.current, 0, GASTOS, { delay: 1300, duracion: 900, formato: money }),
      contar(hor.current, 0, HORMIGA, { delay: 2500, duracion: 700, formato: money }),
      contar(disp.current, 0, 1150, { delay: 3300, duracion: 650, formato: money }),
    ];
    return () => paradas.forEach(p => p());
  }, [fase]);

  const tarjetas = [
    { label: 'Ingresos', value: money(18400), color: 'bg-green-700' },
    { label: 'Gastos', value: money(GASTOS), color: 'bg-red-700', ref: gas, anim: 'hd-card-gas' },
    { label: 'Disponible hoy', value: money(1150), color: 'bg-blue-700', ref: disp, anim: 'hd-card-disp', halo: true },
  ];

  return (
    <div ref={raiz} data-fase={fase || undefined} className="relative max-w-3xl mx-auto mt-14">
      <div aria-hidden="true" className="hd-glow absolute -inset-x-6 -inset-y-8 -z-10 rounded-[2rem] bg-[radial-gradient(60%_60%_at_50%_45%,rgba(59,130,246,0.22),transparent)] dark:bg-[radial-gradient(60%_60%_at_50%_45%,rgba(99,102,241,0.28),transparent)]" />
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-slate-700 overflow-hidden" role="group" aria-label="Ejemplo ilustrativo de MONIMEX: movimientos categorizados, aviso de gastos hormiga y disponible hoy">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-3 flex items-center justify-between">
          <span className="text-white text-xs font-semibold">Ejemplo ilustrativo · datos de muestra</span>
          <span className="text-white text-xs">Este mes</span>
        </div>
        <div className="p-4 sm:p-6 text-left">
          <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-5">
            {tarjetas.map(s => (
              <div key={s.label} className={`${s.anim || ''} relative ${s.color} rounded-xl p-3 sm:p-4 text-white`}>
                <div className="text-xs mb-1">{s.label}</div>
                <div ref={s.ref} className="text-lg sm:text-xl font-bold tabular-nums">{s.value}</div>
                {s.halo && <span aria-hidden="true" className="hd-halo absolute inset-0 rounded-xl ring-2 ring-white/70 opacity-0" />}
              </div>
            ))}
          </div>
          <ul>
            {FILAS.map((t, i) => (
              <li key={t.name} style={{ '--i': i }} className="hd-row-in flex items-center justify-between gap-3 py-2 border-b border-gray-100 dark:border-slate-700/50">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{t.name}</p>
                  <span style={{ '--i': i }} className="hd-chip inline-block mt-0.5 text-[10px] leading-none font-semibold px-1.5 py-1 rounded-md bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">{t.cat}</span>
                </div>
                <span className="text-sm font-semibold shrink-0 text-red-700 dark:text-red-400 tabular-nums">{t.amount}</span>
              </li>
            ))}
          </ul>
          <div className="hd-insight mt-4 flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700/60 px-4 py-3">
            <span aria-hidden="true" className="text-xl leading-none">🐜</span>
            <p className="text-sm text-amber-900 dark:text-amber-100">
              <strong>Gastos hormiga:</strong> 14 compras chicas suman <span ref={hor} className="inline-block min-w-[6ch] font-bold tabular-nums">{money(HORMIGA)}</span> este mes (≈ {money(HORMIGA * 12)} al año).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
